import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function SearchCreators() {
  const navigate = useNavigate();

  // exclude self
  const [currentUserId, setCurrentUserId] = useState(null);

  // form inputs (edit without triggering a search)
  const [keyword, setKeyword] = useState("");
  const [role, setRole] = useState("");
  const [genre, setGenre] = useState("");
  const [tool, setTool] = useState("");
  const [availability, setAvailability] = useState("");

  // applied filters (we search when these change — but only after Search button)
  const [applied, setApplied] = useState({
    keyword: "",
    role: "",
    genre: "",
    tool: "",
    availability: "",
  });

  // control flags / UI state
  const [hasSearched, setHasSearched] = useState(false);
  const [validationErr, setValidationErr] = useState("");

  // mode + data
  const [mode, setMode] = useState("best");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const [total, setTotal] = useState(0);

  // get session (to hide self)
  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setCurrentUserId(session?.user?.id || null);
    })();
  }, []);

  // fetch when: hasSearched && (applied OR page OR mode) change
  useEffect(() => {
    let live = true;
    const run = async () => {
      if (!hasSearched) return; // gate: do nothing until user clicks Search

      setLoading(true);

      let q = supabase
        .from("creator_profiles")
        .select(
          "user_id, name, role, genres, tools, availability, profile_image_url, tagline, bio, created_at",
          { count: "exact" }
        )
        .neq("role", "Admin")
        .neq("role", "OTT Buyer")
        .order("created_at", { ascending: false });

      if (currentUserId) q = q.neq("user_id", currentUserId);

      const kw = applied.keyword?.trim();
      if (kw) {
        q = q.or(
          `name.ilike.%${kw}%,tagline.ilike.%${kw}%,bio.ilike.%${kw}%,genres.ilike.%${kw}%,tools.ilike.%${kw}%`
        );
      }
      if (applied.role?.trim()) q = q.ilike("role", `%${applied.role}%`);
      if (applied.genre?.trim()) q = q.ilike("genres", `%${applied.genre}%`);
      if (applied.tool?.trim()) q = q.ilike("tools", `%${applied.tool}%`);
      if (applied.availability?.trim())
        q = q.ilike("availability", `%${applied.availability}%`);

      const from = page * pageSize;
      const to = from + pageSize - 1;
      q = q.range(from, to);

      const { data, error, count } = await q;

      if (!live) return;

      if (error) {
        console.error("Search error:", error.message);
        setRows([]);
        setTotal(0);
      } else {
        const out = mode === "discover" ? shuffle(data || []) : data || [];
        setRows(out);
        setTotal(count || 0);
      }

      setLoading(false);
    };

    run();

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, applied, page, mode, currentUserId]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total]
  );

  // ---- Actions ----
  const passesValidation = () => {
    // At least one field must have 3+ alphabetic characters
    const fields = [keyword, role, genre, tool, availability];
    const ok = fields.some((f) => /[A-Za-z]{3,}/.test(f || ""));
    if (!ok) {
      setValidationErr(
        "Enter at least 3 letters in any field (or use a Quick Pick), then press Search."
      );
      return false;
    }
    setValidationErr("");
    return true;
  };

  const handleSearch = () => {
    if (!passesValidation()) return;
    setPage(0);
    setApplied({ keyword, role, genre, tool, availability });
    setHasSearched(true);
  };

  const handleReset = () => {
    setKeyword("");
    setRole("");
    setGenre("");
    setTool("");
    setAvailability("");
    setApplied({
      keyword: "",
      role: "",
      genre: "",
      tool: "",
      availability: "",
    });
    setHasSearched(false);
    setRows([]);
    setTotal(0);
    setPage(0);
    setValidationErr("");
  };

  return (
    <div className="min-h-full bg-transparent text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-cyan-300 md:text-2xl">
              🔎 Find People to Collaborate With
            </h2>
            <p className="mt-1 text-xs md:text-sm text-slate-300">
              Search by keyword or filter by role, genres, tools, and availability to
              discover your best collaborators.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-3 py-1 text-[11px] font-medium text-indigo-100 shadow-sm shadow-indigo-500/30">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            Smart filtering active
          </div>
        </div>

        {/* Filters card */}
        <div className="mt-5 rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          {/* Filter grid */}
          <div className="grid gap-4 md:grid-cols-5 md:gap-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                Keyword
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none ring-0 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                placeholder="name, tagline, bio, genres, tools…"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                Primary Role / Skill
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-400"
                placeholder="e.g., AI Video Creator, Scriptwriter…"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                Genre
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-400"
                placeholder="e.g., Sci-Fi, Horror, Kids"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                Tool
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-400"
                placeholder="e.g., RunwayML, Pika Labs, Kling"
                value={tool}
                onChange={(e) => setTool(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                Availability
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-400"
                placeholder="e.g., Weeknights, Weekends"
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
              />
            </div>
          </div>

          {/* Quick picks */}
          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-300">
              Quick Picks (optional)
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {/* Roles */}
              <button
                type="button"
                onClick={() => setRole("AI Video Creator")}
                className="rounded-full border border-slate-600 bg-fuchsia-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-fuchsia-500/30"
              >
                AI Video Creator
              </button>
              <button
                type="button"
                onClick={() => setRole("Scriptwriter")}
                className="rounded-full border border-slate-600 bg-fuchsia-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-fuchsia-500/30"
              >
                Scriptwriter
              </button>
              <button
                type="button"
                onClick={() => setRole("Voice Artist")}
                className="rounded-full border border-slate-600 bg-fuchsia-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-fuchsia-500/30"
              >
                Voice Artist
              </button>
              <button
                type="button"
                onClick={() => setRole("Indie Filmmaker")}
                className="rounded-full border border-slate-600 bg-fuchsia-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-fuchsia-500/30"
              >
                Indie Filmmaker
              </button>
              <button
                type="button"
                onClick={() => setRole("Funder")}
                className="rounded-full border border-slate-600 bg-fuchsia-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-fuchsia-500/30"
              >
                Funder
              </button>

              {/* Genres */}
              <button
                type="button"
                onClick={() => setGenre("Sci-Fi")}
                className="rounded-full border border-slate-600 bg-sky-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-sky-500/30"
              >
                Sci-Fi
              </button>
              <button
                type="button"
                onClick={() => setGenre("Horror")}
                className="rounded-full border border-slate-600 bg-sky-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-sky-500/30"
              >
                Horror
              </button>
              <button
                type="button"
                onClick={() => setGenre("Kids")}
                className="rounded-full border border-slate-600 bg-sky-500/15 px-3 py-1 text-[11px] text-slate-100 hover:bg-sky-500/30"
              >
                Kids
              </button>

              {/* Tools */}
              <button
                type="button"
                onClick={() => setTool("RunwayML")}
                className="rounded-full border border-slate-600 bg-emerald-500/10 px-3 py-1 text-[11px] text-slate-100 hover:bg-emerald-500/25"
              >
                RunwayML
              </button>
              <button
                type="button"
                onClick={() => setTool("Pika Labs")}
                className="rounded-full border border-slate-600 bg-emerald-500/10 px-3 py-1 text-[11px] text-slate-100 hover:bg-emerald-500/25"
              >
                Pika Labs
              </button>
              <button
                type="button"
                onClick={() => setTool("Kling")}
                className="rounded-full border border-slate-600 bg-emerald-500/10 px-3 py-1 text-[11px] text-slate-100 hover:bg-emerald-500/25"
              >
                Kling
              </button>
              <button
                type="button"
                onClick={() => setTool("Midjourney")}
                className="rounded-full border border-slate-600 bg-emerald-500/10 px-3 py-1 text-[11px] text-slate-100 hover:bg-emerald-500/25"
              >
                Midjourney
              </button>
            </div>
            <p className="mt-1 text-[11px] text-indigo-200">
              Tip: Click any pill to auto-fill filters. You can still type your own terms.
            </p>
          </div>

          {/* Buttons + mode + status */}
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="inline-flex items-center rounded-xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-fuchsia-500/40 transition hover:brightness-110 disabled:opacity-60"
              >
                {loading ? "Searching…" : "Search"}
              </button>
              <button
                onClick={handleReset}
                disabled={loading}
                className="inline-flex items-center rounded-xl border border-slate-500 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-100 hover:bg-slate-800 disabled:opacity-50"
              >
                Reset
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-[11px] text-slate-300">
                <span className="text-slate-400">Sort:</span>
                <button
                  type="button"
                  onClick={() => {
                    setMode("best");
                    if (hasSearched) setPage(0);
                  }}
                  className={`rounded-full border px-3 py-1 text-[11px] ${
                    mode === "best"
                      ? "border-fuchsia-400 bg-fuchsia-500/30 text-white"
                      : "border-slate-600 bg-slate-900 text-slate-100"
                  }`}
                >
                  Best Match
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("discover");
                    if (hasSearched) setPage(0);
                  }}
                  className={`rounded-full border px-3 py-1 text-[11px] ${
                    mode === "discover"
                      ? "border-fuchsia-400 bg-fuchsia-500/30 text-white"
                      : "border-slate-600 bg-slate-900 text-slate-100"
                  }`}
                >
                  Discover
                </button>
              </div>

              <span className="text-[11px] text-slate-400 md:ml-auto">
                {!hasSearched
                  ? "Not searched yet."
                  : loading
                  ? "Searching…"
                  : `Found ${total} creator${total === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>

          {validationErr && (
            <div className="mt-2 text-[11px] text-rose-400">{validationErr}</div>
          )}
        </div>

        {/* Results table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/80 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-800/90 text-[11px] uppercase tracking-wide text-slate-300">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Genres</th>
                <th className="px-4 py-3">Tools</th>
                <th className="px-4 py-3">Availability</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r, i) => {
                const genres = (r.genres || "")
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean)
                  .slice(0, 3);
                const tools = (r.tools || "")
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean)
                  .slice(0, 3);

                return (
                  <tr
                    key={r.user_id || i}
                    className="cursor-pointer border-t border-slate-800/70 transition hover:bg-slate-800/75"
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-3">
                        <img
                          src={r.profile_image_url || "/user.png"}
                          alt="avatar"
                          className="h-8 w-8 rounded-full border border-cyan-400/50 object-cover"
                        />
                        <div>
                          <div className="text-sm font-semibold text-slate-100">
                            {r.name || "Unknown"}
                          </div>
                          {r.tagline && (
                            <div className="mt-0.5 text-[11px] text-slate-400">
                              {r.tagline}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-slate-100">
                      {r.role || "-"}
                    </td>
                    <td className="px-4 py-3 align-top text-xs">
                      {genres.length ? (
                        <div className="flex flex-wrap gap-1">
                          {genres.map((g, idx) => (
                            <span
                              key={idx}
                              className="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-200"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-xs">
                      {tools.length ? (
                        <div className="flex flex-wrap gap-1">
                          {tools.map((t, idx) => (
                            <span
                              key={idx}
                              className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-slate-100">
                      {r.availability || "-"}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => navigate(`/profile/${r.user_id}`)}
                        className="inline-flex items-center rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm shadow-fuchsia-500/40 hover:brightness-110"
                      >
                        🔍 View Profile
                      </button>
                    </td>
                  </tr>
                );
              })}

              {hasSearched && !loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-[11px] text-slate-400"
                  >
                    No matches yet — try a broader keyword or a Quick Pick above.
                  </td>
                </tr>
              )}

              {!hasSearched && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-[11px] text-slate-400"
                  >
                    Enter at least 3 letters in any field (or click a Quick Pick), then
                    press <span className="font-semibold">Search</span>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="mt-4 flex flex-col items-center justify-between gap-3 text-[11px] text-slate-300 md:flex-row">
          <div>
            Page {page + 1} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!hasSearched || page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center rounded-xl border border-slate-500 px-3 py-1.5 text-[11px] text-slate-100 hover:bg-slate-800 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={!hasSearched || page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center rounded-xl border border-slate-500 px-3 py-1.5 text-[11px] text-slate-100 hover:bg-slate-800 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// local utils
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]; 
  }
  return a;
}
